import { Component, Inject, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AuthRoleCheckService } from "../auth-role-check-service";
import {
  Account,
  AuthenticatedUser,
  SideNavCollection,
} from "@aveva/connect-web-core";
import { TranslateService } from "@ngx-translate/core";
import { ManageBatchJobsService } from "../batch-jobs/manage-batch-jobs.service";
import { CONFIGURATION, Configuration } from "src/configuration";
import { HttpClient } from "@angular/common/http";
import { WorkspaceService, WorkspaceUser } from "../workspaces/workspace.service";
import { AppStreamService } from "../appstream/appstream.service";
import { FileTransferService } from "../filetransfer/filetransfer.service";
import { firstValueFrom, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

/*
 * This static utility caches sidebar links and account icons to prevent re-rendering
 * when navigating between routes.
 *
 * Note: Used by RootComponent for optimization.
 *       It is also imported in root.component.spec.ts to reset cache
 *       between tests to ensure test isolation.
 */

export class NavigationCache {
  private static links: SideNavCollection[] | null = null;
  private static iconCache: { [accountId: string]: string } = {};

  static getLinks(): SideNavCollection[] | null {
    return this.links;
  }

  static setLinks(links: SideNavCollection[]): void {
    this.links = links;
  }

  static hasLinks(): boolean {
    return this.links !== null;
  }

  static getIcon(accountId: string): string | null {
    return this.iconCache[accountId] || null;
  }

  static setIcon(accountId: string, icon: string): void {
    this.iconCache[accountId] = icon;
  }

  static reset(): void {
    this.links = null;
    this.iconCache = {};
  }
}

@Component({
  selector: "app-root",
  templateUrl: "./root.component.html",
  styleUrls: ["./root.component.scss"],
})
export class RootComponent implements OnInit, OnDestroy {
  user: AuthenticatedUser;
  account?: Account;
  accountIcon: string;
  links: SideNavCollection[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private translate: TranslateService,
    private manageBatchJobsService: ManageBatchJobsService,
    private workspaceService: WorkspaceService,
    private appStreamService: AppStreamService,
    private fileTransferService: FileTransferService,
    private authRoleCheckService: AuthRoleCheckService,
    private http: HttpClient,
    @Inject(CONFIGURATION) private configuration: Configuration
  ) { }

  async ngOnInit() {
    this.route.data
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (data: { user: AuthenticatedUser; account: Account }) => {
        if (!data.user || !data.account) return;

        this.user = data.user;
        this.account = data.account;

        if (NavigationCache.hasLinks()) {
          this.links = NavigationCache.getLinks();
        } else {
          await this.createLinks();
        }

        const cachedIcon = NavigationCache.getIcon(data.account.id);
        if (cachedIcon) {
          this.accountIcon = cachedIcon;
        } else {
          await this.getIcon(data.account);
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async createLinks(): Promise<void> {
    const links: SideNavCollection[] = [
      {
        label: this.translate.instant("home"),
        icon: "navigation--home",
        routerLink: this.createNavRouterLink("/streaming/home"),
      },
    ];

    const isAccountAdmin = await this.authRoleCheckService.isAccountAdmin(this.user);
    const isAdminSuperUser = await this.authRoleCheckService.isAdminSuperUser(this.user);

    if (
      // Here we give either Account Admin or a user with Admin Superuser role to access both Manage Desktops and Batch Jobs
      // Batch Jobs is a separate feature, so if these conditions change, these should be changed in /batch-jobs/batch-jobs-feature-access.guard.ts as well
      isAccountAdmin || isAdminSuperUser
    ) {
      const appStreamFeatureEnabled = await firstValueFrom(this.appStreamService.featureEnabled());

      let workspaceUsers: WorkspaceUser[] = [];
      let workspaceFeatureEnabled = false;

      try {
        workspaceFeatureEnabled = await firstValueFrom(this.workspaceService.featureEnabled());

        if (workspaceFeatureEnabled) {
          workspaceUsers = [...(await firstValueFrom(this.workspaceService.getWorkspaces()))];
        }
      } catch (error) {
        workspaceFeatureEnabled = false;
      }

      if (
        (appStreamFeatureEnabled && workspaceFeatureEnabled && workspaceUsers.length > 0) ||
        (workspaceFeatureEnabled && !appStreamFeatureEnabled)
      ) {
        links.push({
          label: this.translate.instant("workspaces.admin.page_title"),
          icon: "content--monitor",
          routerLink: this.createNavRouterLink("/workspaces/admin"),
        });
      }

      // Check batch jobs feature and add link if enabled
      const batchJobsFeatureEnabled = await firstValueFrom(this.manageBatchJobsService.featureEnabled());
      if (batchJobsFeatureEnabled) {
        links.push({
          label: this.translate.instant("batch_jobs.manage_batch_jobs.page_title"),
          icon: "content--schedule",
          routerLink: this.createNavRouterLink("/batchjobs/managebatchjobs"),
        });
      }

      // Check file transfer feature and add link if enabled
      const fileTransferFeatureEnabled = await firstValueFrom(this.fileTransferService.featureEnabled());
      if (fileTransferFeatureEnabled) {
        links.push({
          label: this.translate.instant("filetransfer.page_title"),
          icon: "file--file-transfer",
          routerLink: this.createNavRouterLink("/filetransfer/getfile"),
        });
      }
    }

    NavigationCache.setLinks(links);
    this.links = links;
  }

  private async getIcon(account: Account): Promise<void> {
    try {
      const url = `${this.configuration.apis.am}/accounts/${account.id}/icon`;
      const icon = await firstValueFrom(this.http.get(url, {
        responseType: "text",
      }));
      if (icon) {
        this.accountIcon = `data:image/png;base64,${icon}`;
        NavigationCache.setIcon(account.id, this.accountIcon);
      }
    } catch (error) {
      // Silently handle icon loading errors
    }

  }

  private createNavRouterLink(path: string): string[] {
    return [`#${path}`];
  }
}